import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../lib/supabase";
import posthog from "posthog-js";
import {
  PartnerHero,
  PartnerWhy,
  PartnerProcess,
  PartnerForm,
  PartnerFaq,
  PartnerClosing,
} from "@/components/partners/PartnerSections";

/** Anchor the hero and closing CTAs scroll to. */
const PROPOSE_FORM_ID = "immobilie-vorschlagen";

const Owners = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDe = language === "de";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const formValues = {
        name: formData.get("name") as string,
        email: formData.get("email") as string,
        phone: formData.get("phone") as string,
        address: formData.get("address") as string,
        propertyType: formData.get("propertyType") as string,
        units: formData.get("units") as string,
        area: formData.get("area") as string,
        rent: formData.get("rent") as string,
        lease: formData.get("lease") as string,
        message: formData.get("message") as string,
      };
      const { error } = await supabase.functions.invoke(
        "send-property-submission-email",
        {
          body: { ...formValues, language },
        }
      );
      if (error) throw error;
      // Owner-lead conversion on the /developers funnel. PostHog attaches the
      // captured utm_* / campaign params automatically, so this is enough to
      // measure paid→lead by channel, split by account_type.
      posthog.capture("owner lead submitted", {
        account_type: "owner",
        property_type: formValues.propertyType,
        units: formValues.units,
      });
      toast({
        title: t("owners.form.success"),
        description: "We'll get back to you within 24 hours!",
        duration: 5000,
      });
      (e.target as HTMLFormElement).reset();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description:
          error instanceof Error && error.message
            ? error.message
            : "Failed to submit. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () =>
    document
      .getElementById(PROPOSE_FORM_ID)
      ?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white">
      <PartnerHero
        de={isDe}
        onPropose={scrollToForm}
        onContact={scrollToForm}
      />
      <PartnerWhy de={isDe} />
      <PartnerProcess de={isDe} />
      <PartnerForm
        de={isDe}
        onSubmit={handleSubmit}
        submitting={isSubmitting}
        formId={PROPOSE_FORM_ID}
      />
      <PartnerFaq de={isDe} />
      <PartnerClosing de={isDe} onPropose={scrollToForm} />
    </div>
  );
};

export default Owners;
